import {
	Plugin,
	ButtonView,
	View,
	Dialog
} from 'ckeditor5';

import FormView from './ui/formView.js'
import youtubeIcon from '../theme/icons/youtube.svg';

/**
 * The Youtube plugin. It introduces the `dialog` view.
 *
 * It uses the
 * {@link module:ui/dialog/dialogview~DialogView dialog plugin}.
 *
 * @extends module:core/plugin~Plugin
 */
export default class Youtube extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [ Dialog ]
	}

	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'Youtube';
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		const t = editor.t;

		this.dialog = this.editor.plugins.get( 'Dialog' );

		// Register the "youtube" button, so it can be displayed in the toolbar.
		editor.ui.componentFactory.add( 'youtube', locale => {
			const view = new ButtonView( locale );

			view.set( {
				label: t( 'Insert YouTube media' ),
				icon: youtubeIcon,
				tooltip: true
			} );

			// Insert a text into the editor after clicking the button.
			this.listenTo( view, 'execute', () => {
				const t = this.editor.t;
				const formView = this._createFormView()
				const instructionOneView = this._createTextView("Enter the YouTube Video ID (e.g Wfg6kXRLb5E) to insert into the document.")
				const instructionTwoView = this._createTextView(" You can also specify a start and end time for the video.")

				const modalBodyView = new View( locale );

				modalBodyView.setTemplate({
					tag: 'div',
					attributes: {
						style: {
							padding: 'var(--ck-spacing-large)',
						}
					},
					children: [
						instructionOneView,
						instructionTwoView,
						formView,
					]
				})

				// If the button is turned on, hide the modal.
				if ( view.isOn ) {
					this.dialog.hide();
					view.isOn = false;

					return;
				}
				
				view.isOn = true;

				this.dialog.show( {
					isModal: true,
					title: t("Insert YouTube Media"),
					content: modalBodyView,
					onHide() { view.isOn = false; }
				} )
			} );

			return view;
		} );
	}

	_createTextView( content ) {
		const editor = this.editor;
		const textView = new View( editor.locale )

		textView.setTemplate({
			tag: "p",
			children: [ content ]
		})

		return textView;
	}

	/**
	 * Creates the {@link module:youtube/ui/formView~FormView} instance.
	 *
	 * @private
	 * @returns {module:youtube/ui/formView~FormView} The youtube form view instance.
	 */
	_createFormView() {
		const editor = this.editor;
		const model = editor.model;
		const dialog = editor.plugins.get('Dialog');

		const formView = new FormView( editor.locale, dialog );

		// Execute insert YouTube media after clicking the "Save" button.
		this.listenTo( formView, 'submit', () => {
			const { value: videoId } = formView.videoIdInputView.fieldView.element;
			const { value: startTime } = formView.startTimeInputView.fieldView.element;
			const { value: endTime } = formView.endTimeInputView.fieldView.element;

			// TODO: VALIDATE HERE
			const url = `https://www.youtube.com/embed/${videoId}?rel=0&start=${startTime}&end=${endTime}`
			const findOptimalPosition = true;
			
			model.change( writer => {
				const mediaElement = writer.createElement( 'media', { url } );
		
				model.insertObject( mediaElement, undefined, null, {
					setSelection: 'on',
					findOptimalPosition: findOptimalPosition ? 'auto' : undefined
				} );
			} );

			this.dialog.hide();
		} );

		// Hide the panel after clicking the "Cancel" button.
		this.listenTo( formView, 'cancel', () => {
			this.dialog.hide();
		} );

		return formView;
	}
}
